import React, { useState, useEffect, useMemo } from 'react'
import AdminDashboardLayout from './superadmin/AdminDashboardLayout'
import { corporateEmployeeStatusAPI, corporateShowEmployeesAPI } from '../redux/slices/CorporateAddEmployee';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { errorHandler, base } from '../constants/base';
import AdminDataTable from '../components/AdminComponents/AdminDataTable';
import './superadmin/ModuleAnalytics.css';

const ViewEmployee = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const employeeLoading = useSelector((state) => state?.coporateAddEmployeSlice?.employeeLoading);
    const allEmployees = useSelector((state) => state?.coporateAddEmployeSlice?.allEmployees);
    const analytics = useSelector((state) => state?.coporateAddEmployeSlice?.analytics);
    const pagination = useSelector((state) => state?.coporateAddEmployeSlice?.pagination);
    const [currentPage, setCurrentPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('');
    const param = useParams();

    const fetchEmployees = async (page = currentPage) => {
        try {
            const corporate = {
                id: param?.id,
                page: page
            }
            await dispatch(corporateShowEmployeesAPI(corporate));
        } catch (error) {
            console.error("Error fetching employees:", error);
        }
    };

    useEffect(() => {
        fetchEmployees(currentPage);
    }, [param?.id, currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleChangeStatus = async (employee_id, status, corporate_id) => {
        const res = await dispatch(corporateEmployeeStatusAPI({ coporate_id: corporate_id, employee_id, status }));

        if (res.type === 'corporateEmployeeStatusAPI/fulfilled') {
            if (res.payload.success || res.payload.status || res.payload.data.status === 200) {
                fetchEmployees(currentPage);
                toast.success("Employee status updated successfully");
            } else {
                errorHandler(res.payload.errors);
            }
        } else if (res.type === 'corporateEmployeeStatusAPI/rejected') {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
    };

    // Filter employees based on status
    const filteredEmployees = useMemo(() => {
        if (!statusFilter) return allEmployees;
        return allEmployees?.filter(emp => emp.status === statusFilter) || [];
    }, [allEmployees, statusFilter]);

    const columns = useMemo(() => [
        {
            Header: 'Employee',
            accessor: 'name',
            Cell: ({ row }) => (
                <div className="admin-dt-user-cell">
                    {row.original?.images?.[0]?.path ? (
                        <img
                            src={`${base.imageUrl}${row.original.images[0].path}`}
                            alt={row.original.name}
                            className="admin-dt-user-avatar"
                        />
                    ) : (
                        <div className="admin-dt-user-placeholder">
                            <i className="fas fa-user"></i>
                        </div>
                    )}
                    <div className="admin-dt-user-info">
                        <p className="admin-dt-user-name">{row.original?.name || '-'}</p>
                        <p className="admin-dt-user-email">{row.original?.email || '-'}</p>
                    </div>
                </div>
            ),
        },
        {
            Header: 'Phone',
            accessor: 'phone_no',
            className: 'text-center',
            Cell: ({ value }) => value || '-',
        },
        {
            Header: 'Status',
            accessor: 'status',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ row }) => (
                <select
                    value={row.original?.status}
                    onChange={(e) => handleChangeStatus(row.original?.id, e.target.value, row.original?.parent_id)}
                    className="admin-dt-select"
                >
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Block">Block</option>
                </select>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'id',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ row }) => (
                <div className="admin-dt-actions">
                    <Link
                        to={`/admin/employees/view/${row.original?.id}/${row.original?.parent_id}`}
                        className="admin-dt-action-btn view"
                    >
                        <i className="fas fa-eye"></i>
                    </Link>
                </div>
            ),
        },
    ], []);

    const analyticsCards = [
        { title: 'Total Employees', value: analytics?.total || 0, icon: 'fa-solid fa-users', color: '#EF4444' },
        { title: 'Active', value: analytics?.active || 0, icon: 'fa-solid fa-user-check', color: '#60A5FA' },
        { title: 'Pending', value: analytics?.pending || 0, icon: 'fa-solid fa-user-clock', color: '#4ADE80' },
        { title: 'Blocked', value: analytics?.blocked || 0, icon: 'fa-solid fa-user-xmark', color: '#FBBF24' },
    ];

    const filterElements = (
        <>
            <button
                className="admin-dt-back-btn"
                onClick={() => navigate('/admin/corporates')}
            >
                <i className="fas fa-arrow-left"></i>
                Back
            </button>
            <select value={statusFilter} onChange={handleStatusFilter} className="admin-dt-filter-select">
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="Active">Active</option>
                <option value="Block">Blocked</option>
            </select>
        </>
    );

    return (
        <AdminDashboardLayout>
            {/* Analytics Cards */}
            <div className="module-analytics-grid">
                {analyticsCards.map((card, index) => (
                    <div key={index} className="module-analytics-card">
                        <div className="module-analytics-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                            <i className={card.icon}></i>
                        </div>
                        <div className="module-analytics-content">
                            <p className="module-analytics-label">{card.title}</p>
                            <h3 className="module-analytics-value">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <AdminDataTable
                columns={columns}
                data={filteredEmployees || []}
                title="Corporate Employees"
                icon="fa-solid fa-users"
                loading={employeeLoading}
                searchable={false}
                filters={filterElements}
                serverSidePagination={{
                    currentPage: pagination?.current_page || 1,
                    totalPages: pagination?.total_pages || 1,
                    total: pagination?.total || 0,
                    perPage: pagination?.per_page || 10,
                    onPageChange: handlePageChange,
                }}
            />
        </AdminDashboardLayout>
    );
};

export default ViewEmployee;
